// Path: frontend/src/lib/api/users.ts
import { api } from './core';
import type {
  School, User, PermissionType, SchoolPermissionSummary,
  SchoolWithUsersPermission, AssignedCourse,
  SchoolTime, SchoolTimeCreate, SchoolTimeUpdate,
  City, District, CourseType, SchoolCreateWithConfigResult, BatchImportResult
} from './core';

// ==========================================
// 📋 Extended Types for Deleted/Restore/Transfer
// ==========================================

// 已删除学校 + 其下已删除用户统计（文档 3.1）
export interface SchoolWithDeletedUsers extends School {
  deleted_users_count: number;
  deleted_user_ids: number[];
}

// 恢复学校的返回值（文档 3.3）
export interface RestoreSchoolResult {
  message: string;
  restored_users_count: number;
  restored_user_ids: number[];
  conflict_users: Array<{
    user_id: number;
    username: string;
    conflict_user_id: number;
  }>;
}

// 删除学校连带删除用户的返回值（文档 2.2 新增字段）
export interface DeleteSchoolResult {
  message: string;
  deleted_users_count: number;
  deleted_user_ids: number[];
}

// ==========================================
// 🏫 School Management
// ==========================================
export const createSchool = (data: { schoolname: string }) => api.post<School>(`/schools`, data);
export const getSchools = (params?: { skip?: number; limit?: number }) => api.get<School[]>(`/schools`, { params });
export const getSchoolDetail = (schoolId: number) => api.get<School>(`/schools/${schoolId}`);
export const updateSchool = (schoolId: number, data: { schoolname?: string; is_deleted?: boolean }) => api.put<School>(`/schools/${schoolId}`, data);
export const deleteSchool = (schoolId: number) => api.delete<DeleteSchoolResult>(`/schools/${schoolId}`);

// ==========================================
// 🕐 School Times (每周上课班次) Management
// ==========================================
export const getSchoolTimes = (schoolId: number) =>
  api.get<SchoolTime[]>(`/schools/${schoolId}/times`);

export const createSchoolTime = (schoolId: number, data: SchoolTimeCreate) =>
  api.post<SchoolTime>(`/schools/${schoolId}/times`, data);

export const updateSchoolTime = (schoolId: number, timeId: number, data: SchoolTimeUpdate) =>
  api.put<SchoolTime>(`/schools/${schoolId}/times/${timeId}`, data);

export const deleteSchoolTime = (schoolId: number, timeId: number) =>
  api.delete<{ message: string }>(`/schools/${schoolId}/times/${timeId}`);

// ==========================================
// 👥 User Management
// ==========================================
export const createUser = (data: { username: string; password: string; role?: 'sys' | 'admin' | 'teacher' | 'student'; school_id?: number; avatar_url?: string; memo?: string; }) => api.post<User>(`/users`, data);
export const getUsers = (params?: { skip?: number; limit?: number; school_id?: number }) => api.get<User[]>(`/users`, { params });
export const getUserDetail = (userId: number) => api.get<User>(`/users/${userId}`);
export const updateUser = (userId: number, data: { username?: string; password?: string; role?: 'sys' | 'admin' | 'teacher' | 'student'; school_id?: number; avatar_url?: string; memo?: string; }) => api.put<User>(`/users/${userId}`, data);
export const deleteUser = (userId: number) => api.delete<{ message: string }>(`/users/${userId}`);
export const uploadAvatarGen = (file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post<User>(`/users/me/avatar/gen`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};

export const getRoles = () => api.get<string[]>(`/users/roles`);
export const getMemos = () => api.get<string[]>(`/users/memos`);

// ==========================================
// ️ Permission Management
// ==========================================
export const getPermissionCourses = () => api.get<any[]>(`/permissions/courses`);
export const getCourseSchoolPermissions = (courseId: number) => api.get<SchoolPermissionSummary[]>(`/permissions/courses/${courseId}/school-permissions`);
export const getCourseUserPermissions = (courseId: number, schoolId: number) => api.get<SchoolWithUsersPermission[]>(`/permissions/courses/${courseId}/school/${schoolId}/user-permissions`);
export const getStageSchoolPermissions = (stageId: number) => api.get<SchoolPermissionSummary[]>(`/permissions/stages/${stageId}/school-permissions`);
export const getStageUserPermissions = (stageId: number, schoolId: number) => api.get<SchoolWithUsersPermission[]>(`/permissions/stages/${stageId}/school/${schoolId}/user-permissions`);
export const getLessonSchoolPermissions = (lessonId: number) => api.get<SchoolPermissionSummary[]>(`/permissions/lessons/${lessonId}/school-permissions`);
export const getLessonUserPermissions = (lessonId: number, schoolId: number) => api.get<SchoolWithUsersPermission[]>(`/permissions/lessons/${lessonId}/school/${schoolId}/user-permissions`);
export const getSchoolAssignedCourses = (schoolId: number) => api.get<AssignedCourse[]>(`/permissions/schools/${schoolId}/assigned-courses/`);
export const getUserAssignedCourses = (userId: number) => api.get<AssignedCourse[]>(`/permissions/users/${userId}/assigned-courses/`);

export const updateCourseSchoolPermissions = (courseId: number, data: { school_id: number; permission_type: PermissionType }) => api.post<{ message: string }>(`/permissions/courses/${courseId}/school-permissions`, data);
export const updateStageSchoolPermissions = (stageId: number, data: { school_id: number; permission_type: PermissionType }) => api.post<{ message: string }>(`/permissions/stages/${stageId}/school-permissions`, data);
export const updateCourseUserPermissions = (courseId: number, data: { user_id: number; permission_type: PermissionType }) => api.post<{ message: string }>(`/permissions/courses/${courseId}/user-permissions`, data);
export const updateStageUserPermissions = (stageId: number, data: { user_id: number; permission_type: PermissionType }) => api.post<{ message: string }>(`/permissions/stages/${stageId}/user-permissions`, data);
export const updateLessonSchoolPermissions = (lessonId: number, data: { school_id: number; permission_type: PermissionType }) => api.post<{ message: string }>(`/permissions/lessons/${lessonId}/school-permissions`, data);
export const updateLessonUserPermissions = (lessonId: number, data: { user_id: number; permission_type: PermissionType }) => api.post<{ message: string }>(`/permissions/lessons/${lessonId}/user-permissions`, data);
export const deletePermission = (permissionId: number) => api.delete<{ message: string }>(`/permissions/permissions/${permissionId}`);
export const deleteStageUserPermissions = (stageId: number, userId: number) => api.delete<{ message: string }>(`/permissions/stages/${stageId}/users/${userId}`);
export const deleteCourseUserPermissions = (courseId: number, userId: number) => api.delete<{ message: string }>(`/permissions/courses/${courseId}/users/${userId}`);
export const deleteLessonUserPermissions = (lessonId: number, userId: number) => api.delete<{ message: string }>(`/permissions/users/${userId}/lessons/${lessonId}`);

export const changePassword = (data: { old_password: string; new_password: string }) =>
  api.post<{ message: string }>(`/auth/change-password`, data);

// ==========================================
// 🗑️ 已删除数据查询（文档 3.1 / 3.2）
// ==========================================
export const getDeletedSchools = (params?: { skip?: number; limit?: number }) =>
  api.get<SchoolWithDeletedUsers[]>(`/schools/deleted`, { params });

export const getDeletedSchoolDetail = (schoolId: number) =>
  api.get<SchoolWithDeletedUsers>(`/schools/deleted/${schoolId}`);

export const getDeletedUsers = (params?: { skip?: number; limit?: number; school_id?: number }) =>
  api.get<User[]>(`/users/deleted`, { params });

export const searchDeletedUsers = (params: { username: string; school_id?: number }) =>
  api.get<User[]>(`/users/deleted/search`, { params });

// ==========================================
// ♻️ 恢复功能（文档 3.3）
// ==========================================
export const restoreSchool = (schoolId: number) =>
  api.post<RestoreSchoolResult>(`/schools/${schoolId}/restore`);

export const restoreUser = (userId: number, data?: { new_school_id?: number | null }) =>
  api.post<User>(`/users/${userId}/restore`, data ?? {});

// ==========================================
// 🔄 转机构功能（文档 3.4）
// ==========================================
export const transferUser = (userId: number, data: { new_school_id: number | null }) =>
  api.post<User>(`/users/${userId}/transfer`, data);

// ==========================================
// 🏗️ 机构配置创建（文档第 15 章）
// ==========================================
export const getCities = () =>
  api.get<City[]>(`/schools/cities`);

export const getDistricts = (params?: { city_id?: number }) =>
  api.get<District[]>(`/schools/districts`, { params });

// ============= 城市 CRUD（文档第五章） =============
export const createCity = (data: { name: string }) =>
  api.post<City>(`/schools/cities`, data);

export const getCityDetail = (cityId: number) =>
  api.get<City>(`/schools/cities/${cityId}`);

export const updateCity = (cityId: number, data: { name?: string }) =>
  api.put<City>(`/schools/cities/${cityId}`, data);

export const deleteCity = (cityId: number) =>
  api.delete<{
    message: string;
    deleted_city_id: number;
    deleted_district_ids: number[];
    deleted_district_count: number;
    deleted_face_count?: number;
  }>(`/schools/cities/${cityId}`);

// ============= 区县 CRUD（文档第六章） =============
export const createDistrict = (data: { city_id: number; name: string }) =>
  api.post<District>(`/schools/districts`, data);

export const getDistrictDetail = (districtId: number) =>
  api.get<District>(`/schools/districts/${districtId}`);

export const updateDistrict = (districtId: number, data: { city_id?: number; name?: string }) =>
  api.put<District>(`/schools/districts/${districtId}`, data);

export const deleteDistrict = (districtId: number) =>
  api.delete<{ message: string; deleted_district_id: number }>(`/schools/districts/${districtId}`);

export const getCourseTypes = () =>
  api.get<CourseType[]>(`/schools/course-types`);

export const createSchoolWithConfig = (data: {
  city_id: number;
  district_id: number;
  is_trial?: boolean;
  day_of_weeks?: number[];
  start_time?: string;
  end_time?: string;
  course_type_id?: number;
}) => api.post<SchoolCreateWithConfigResult>(`/schools/create-with-config`, data);

// ==========================================
// 📥 批量导入用户（文档第 14 章）
// ==========================================
export const batchImportUsers = (data: {
  school_id: number;
  usernames_raw: string;
  role?: string;
}) => api.post<BatchImportResult>(`/users/batch-import`, data);